import React, { useRef, useEffect, useState } from 'react';
import { CheckCircle, MapPin, Zap, TrendingUp, Sparkles } from 'lucide-react';
import { mockData } from './mock';

const iconComponents = {
  CheckCircle,
  MapPin, 
  Zap,
  TrendingUp
};

// Custom hook for scroll animations
const useScrollAnimation = (triggerOnce = false) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [triggerOnce]);

  return [ref, isVisible];
};

// Animation class generator
const createScrollAnimationClasses = (animationType, isVisible) => {
  const baseClasses = 'transition-all duration-700 ease-out';
  
  switch (animationType) {
    case 'fadeInUp':
      return `${baseClasses} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;
    case 'fadeInLeft':
      return `${baseClasses} ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`;
    case 'fadeInRight':
      return `${baseClasses} ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`;
    case 'scaleIn':
      return `${baseClasses} ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`;
    default:
      return baseClasses;
  }
};

export const StatsSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [statsRef, statsVisible] = useScrollAnimation();

  return (
    <section className="relative py-20 lg:py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/20 via-transparent to-indigo-50/20"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-200/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-200/10 rounded-full blur-3xl"></div>
      
      {/* Floating Particles */}
      <div className="absolute top-20 left-10 w-2 h-2 bg-blue-400/30 rounded-full animate-pulse"></div>
      <div className="absolute top-32 right-20 w-1 h-1 bg-indigo-400/40 rounded-full animate-ping"></div>
      <div className="absolute bottom-20 left-1/3 w-1.5 h-1.5 bg-blue-300/30 rounded-full animate-pulse"></div>
      <div className="absolute bottom-32 right-1/3 w-1 h-1 bg-indigo-300/40 rounded-full animate-ping"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Enhanced Header */}
        <div ref={headerRef} className={`text-center mb-16 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-blue-200/50 rounded-full px-4 py-2 mb-6 shadow-lg">
            <Sparkles size={16} className="text-blue-600" />
            <span className="text-sm font-semibold text-blue-700">Our Impact</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            <span 
              className="bg-clip-text text-transparent"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(99 102 241))' 
              }}
            >
              Numbers That Matter
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Driving innovation and delivering measurable results across Oman's smart city landscape
          </p>
        </div>

        {/* Enhanced Stats Grid */}
        <div ref={statsRef} className={`grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 ${createScrollAnimationClasses('fadeInUp', statsVisible)}`}>
          {mockData.stats.map((stat, index) => {
            const IconComponent = iconComponents[stat.icon];
            
            // Color themes for each stat - matching the image design
            const colorThemes = {
              CheckCircle: {
                bg: 'from-green-500 to-emerald-600',
                text: 'text-green-600',
                hoverText: 'group-hover:text-green-700',
                border: 'border-green-200/50',
                glow: 'from-green-50/30 to-emerald-50/30',
                accent: 'from-green-500 to-emerald-500'
              },
              MapPin: {
                bg: 'from-blue-500 to-indigo-600',
                text: 'text-blue-600',
                hoverText: 'group-hover:text-blue-700',
                border: 'border-blue-200/50',
                glow: 'from-blue-50/30 to-indigo-50/30',
                accent: 'from-blue-500 to-indigo-500'
              },
              Zap: {
                bg: 'from-blue-500 to-purple-600',
                text: 'text-blue-600',
                hoverText: 'group-hover:text-blue-700',
                border: 'border-blue-200/50',
                glow: 'from-blue-50/30 to-purple-50/30',
                accent: 'from-blue-500 to-purple-500'
              },
              TrendingUp: {
                bg: 'from-purple-500 to-pink-600',
                text: 'text-purple-600',
                hoverText: 'group-hover:text-purple-700',
                border: 'border-purple-200/50',
                glow: 'from-purple-50/30 to-pink-50/30',
                accent: 'from-purple-500 to-pink-500'
              }
            };

            const theme = colorThemes[stat.icon];

            return (
              <div 
                key={index} 
                className={`group relative overflow-hidden text-center bg-white/90 backdrop-blur-sm rounded-2xl p-6 lg:p-8 border ${theme.border} shadow-xl hover:shadow-2xl transition-all duration-600 hover:-translate-y-3 hover:scale-[1.02]`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Subtle Background Glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${theme.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`}></div>
                
                {/* Enhanced Icon */}
                <div className={`relative z-10 w-12 h-12 bg-gradient-to-br ${theme.bg} rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-2 transition-all duration-500 shadow-lg`}>
                  <IconComponent size={26} className="text-white group-hover:scale-110 transition-transform duration-300" />
                </div>
                
                {/* Enhanced Content */}
                <div className="relative z-10">
                  <div className={`text-3xl lg:text-4xl font-bold ${theme.text} mb-2 ${theme.hoverText} transition-colors duration-300`}>
                    {stat.value}
                  </div>
                  <div className="text-sm lg:text-base text-gray-800 font-semibold group-hover:text-gray-900 transition-colors">
                    {stat.label}
                  </div>
                </div>
                
                {/* Subtle Bottom Accent */}
                <div className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r ${theme.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-2xl`}></div>
              </div>
            );
          })}
        </div>

        {/* Additional Trust Indicator */}
        <div ref={statsRef} className={`mt-16 text-center ${createScrollAnimationClasses('fadeInUp', statsVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-full px-6 py-3 shadow-lg">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold text-gray-700">Trusted by leading organizations across Oman</span>
          </div>
        </div>
      </div>
    </section>
  );
};